import { addAlertMessage } from "#redux/actions/alertMessages";
import { havePermission } from "#redux/reducers/permissions";
import { getBreadcrumbsObject } from "#routers/breadcrumbsHelper";
import { useLocation, useNavigate, useSearchParams } from "#routers/hooks";
import CalibrationService from "#services/CalibrationService";
import InstrumentService from "#services/InstrumentService";
import useTableState from "#src/hooks/useTableState";
import {
  linkToInlineInstrument,
  linkToInstrumentSummary,
} from "#src/Routers/links";
import { getFrontendTableState } from "#utils/frontendTableActions";
import {
  Button,
  DataTable,
  Filters,
  ItemAction,
  Page,
  Panel,
  useToast,
} from "@validereinc/common-components";
import { downloadLink } from "@validereinc/utilities";
import * as PropTypes from "prop-types";
import React, { useCallback, useState } from "react";
import { connect } from "react-redux";
import ColumnSelect from "../Common/ColumnSelect/ColumnSelect";
import { INSTRUMENT_TYPE_LABEL } from "./InstrumentConstant";
import ArchiveInstrumentModal from "./InstrumentsList/Modals/ArchiveInstrumentModal";
import EditCalibrationModal from "./InstrumentsList/Modals/EditCalibrationModal";
import EditInstrumentDetailsModal from "./InstrumentsList/Modals/EditInstrumentDetailsModal";
import EffectiveDurationModal from "./InstrumentsList/Modals/EffectiveDurationModal";
import useInstrumentsConfig from "./useInstrumentsConfig";
const INSTRUMENTS_LOCAL_STORAGE_KEY = "instruments_selection_headers";

const ITEMS_KEY = "data.results";

const mapStateToProps = ({ permissions }) => {
  return {
    canEditCalibrations: havePermission(permissions)(
      "360:calibrations",
      "write"
    ),
    canEditFieldLabs: havePermission(permissions)("360:field_labs", "write"),
  };
};

const mapDispatchToProps = {
  addAlertMessage,
};

const Instruments = ({
  breadcrumbs,
  canEditCalibrations,
  canEditFieldLabs,
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const { toast } = useToast();

  const [showModal, setShowModal] = useState<string | null>(null);
  const [instrumentDetail, setInstrumentDetail] = useState<any | null>(null);
  const [response, setResponse] = useState<any | undefined>(undefined);

  const instrumentsPageBreadcrumbs = getBreadcrumbsObject(breadcrumbs);

  const onFetchData = useCallback(
    async ({ hardRefresh = false, ...newSearchParams }) => {
      let newResponse: any = response;

      if (hardRefresh || !response) {
        const { page, rowPerPage, sort, sortDirection } = newSearchParams;

        newResponse = await InstrumentService.getInstrumentList({
          page,
          rowPerPage,
          sort: { [sort]: sortDirection },
        });

        setResponse(newResponse);
      }

      return getFrontendTableState({
        data: newResponse,
        itemsKey: ITEMS_KEY,
        query: newSearchParams,
        filterMapping: {
          instrument_type: {
            filter: ({ value, item }) => {
              if (Array.isArray(item)) {
                return item.includes(INSTRUMENT_TYPE_LABEL[value]);
              } else {
                return INSTRUMENT_TYPE_LABEL[value] === item;
              }
            },
          },
          name: {
            filter: ({ value, item }) =>
              value?.toLowerCase()?.includes(item?.toLowerCase()),
          },
        },
      });
    },
    [response]
  );

  const { tableProps, fetchData, data } = useTableState({
    onFetchData,
  });

  const { filters, quickFilters, headers } = useInstrumentsConfig({
    instruments: data?.allItems,
  } as any);

  const [displayHeaders, setDisplayHeaders] = useState(headers);

  const onFiltersChange = (values) => {
    const { quickFilters, instrument_type, ...rest } = values;

    navigate({
      pathname: location.pathname,
      query: {
        ...searchParams,
        ...rest,
        status: quickFilters,
        instrument_type: instrument_type?.map(({ value }) => value),
      },
      replace: true,
    });
  };

  const onRowClick = ({ instrument_type: type, id }) => {
    navigate({
      pathname: ["inline", "inline_virtual", "emission_monitor"].includes(type)
        ? linkToInlineInstrument(type, id)
        : linkToInstrumentSummary(id),
    });
  };

  const onDownloadCSV = () => {
    toast.push({
      intent: "success",
      description: "Your files will start downloading shortly.",
    });

    CalibrationService.getCalibrationCSVDownloadLink().then(({ data }) => {
      downloadLink(data.url);
    });
  };

  const toggleModal = (
    showModal: string | null = null,
    instrumentDetail: any | null = null
  ) => {
    setShowModal(showModal);
    setInstrumentDetail(instrumentDetail);
  };

  const hardRefresh = () => {
    fetchData({ ...searchParams, hardRefresh: true });
  };

  return (
    <>
      <Page
        title="Instruments"
        breadcrumbs={instrumentsPageBreadcrumbs}
      >
        <Filters
          onChange={onFiltersChange}
          filters={filters ?? []}
          quickFilters={quickFilters}
          actions={[
            <ColumnSelect
              key={"column-select"}
              options={headers}
              onChange={setDisplayHeaders}
              value={displayHeaders}
              localStorageKey={INSTRUMENTS_LOCAL_STORAGE_KEY}
            />,
            <Button
              key="export-button"
              variant="primary"
              size="small"
              onClick={onDownloadCSV}
            >
              Export
            </Button>,
          ]}
        />

        <Panel>
          <DataTable
            headers={displayHeaders}
            {...tableProps}
            isLoading={tableProps.loading}
            getItemId={(item) => item.id}
            onRowClick={onRowClick}
            isFluid={false}
            getItemActions={({ item }): ItemAction[] => [
              ...(canEditCalibrations && item.instrument_type !== "inline"
                ? ([
                    {
                      isOverflow: true,
                      label: "Edit Details",
                      buttonProps: {
                        onClick: () => toggleModal("editDetails", item),
                        icon: "pencil-simple",
                      },
                    },
                    {
                      isOverflow: true,
                      label: "Update Calibration Check",
                      buttonProps: {
                        onClick: () => toggleModal("calibration", item),
                      },
                    },
                    {
                      isOverflow: true,
                      label: "Update Effectivity Duration",
                      buttonProps: {
                        onClick: () => toggleModal("effectivityDuration", item),
                      },
                    },
                  ] satisfies ItemAction[])
                : []),
              ...(canEditFieldLabs
                ? ([
                    {
                      isOverflow: true,
                      label: "Archive/Unarchive Instrument",
                      buttonProps: {
                        onClick: () => toggleModal("archive", item),
                        icon: "archive",
                      },
                    },
                  ] satisfies ItemAction[])
                : []),
            ]}
          />
        </Panel>

        <EditCalibrationModal
          show={showModal === "calibration"}
          instrumentDetail={instrumentDetail}
          onCalibrationUpdated={hardRefresh}
          onHide={toggleModal}
          addAlertMessage={() => {}}
        />

        <EditInstrumentDetailsModal
          show={showModal === "editDetails"}
          onHide={toggleModal}
          instrumentId={instrumentDetail?.id}
          instrumentName={instrumentDetail?.name}
          serialNumber={instrumentDetail?.current_serial_number}
          availableSerialNumbers={instrumentDetail?.serial_numbers}
          refetchCalibrationData={hardRefresh}
        />

        <ArchiveInstrumentModal
          show={showModal === "archive"}
          onHide={toggleModal}
          instrumentDetail={instrumentDetail}
          refetchInstruments={hardRefresh}
        />

        <EffectiveDurationModal
          show={showModal === "effectivityDuration"}
          onHide={toggleModal}
          instrumentDetail={instrumentDetail}
          refetchCalibrationData={hardRefresh}
          addAlertMessage={() => {}}
        />
      </Page>
    </>
  );
};

Instruments.propTypes = {
  breadcrumbs: PropTypes.array.isRequired,
  canEditCalibrations: PropTypes.bool,
  canEditFieldLabs: PropTypes.bool,
};

const InstrumentsContainer = connect(
  mapStateToProps,
  mapDispatchToProps
)(Instruments);

export default InstrumentsContainer;
